import type {
  SessionAssignationPolicy,
  SessionAssignationRow,
  SessionAssignationRowValues,
} from "@services/types/session-assignation/sessionAssignationTypes";
import { calendarDate, idSetKey, normalizeTime } from "../adapters/occurrences";

export const canEditAssignation = (policy: SessionAssignationPolicy): boolean =>
  !!policy.permissions.update &&
  !!policy.abilities.can_edit_assignment &&
  policy.courseCanEdit &&
  !policy.isAssignationLocked;

export const isPastOccurrence = (
  row: SessionAssignationRowValues,
  policy: SessionAssignationPolicy,
  weekStartingDate: string,
): boolean => {
  if (!policy.thresholdDate || policy.abilities.can_edit_past_sessions) return false;
  const [date, time = "00:00:00"] = policy.thresholdDate.replace("T", " ").split(" ");
  const thresholdDay = calendarDate(date);
  const thresholdTime = normalizeTime(time.slice(0, 8));
  const eventDate = row.scheduleMode === "scheduled" ? row.date : calendarDate(weekStartingDate);
  const start = row.scheduleMode === "scheduled" ? normalizeTime(row.startingTime) : "00:00:00";
  return eventDate < thresholdDay || (eventDate === thresholdDay && start <= thresholdTime);
};

export const canCreateOccurrence = (policy: SessionAssignationPolicy): boolean =>
  canEditAssignation(policy) &&
  !!policy.permissions.create &&
  !!policy.abilities.can_edit_links_and_sections &&
  !!policy.abilities.can_edit_or_delete_sessions &&
  !!policy.abilities.can_assign_blocks &&
  !!policy.abilities.can_assign_intervals;

export const canDeleteOccurrence = (policy: SessionAssignationPolicy): boolean =>
  canEditAssignation(policy) && !!policy.abilities.can_edit_or_delete_sessions;

export const canPatchOccurrence = (
  row: SessionAssignationRow,
  next: SessionAssignationRowValues,
  policy: SessionAssignationPolicy,
): boolean => {
  if (!canEditAssignation(policy)) return false;
  const abilities = policy.abilities;
  const classroomsChanged =
    idSetKey(row.classrooms.map(item => item.id)) !==
    idSetKey(next.classrooms.map(item => item.id));
  const instructorsChanged =
    idSetKey(row.instructors.map(item => item.id)) !==
    idSetKey(next.instructors.map(item => item.id));
  const scheduleChanged =
    row.scheduleMode !== next.scheduleMode ||
    row.dayId !== next.dayId ||
    row.startingTime !== next.startingTime ||
    row.endingTime !== next.endingTime ||
    row.blocksCountInput !== next.blocksCountInput ||
    idSetKey(row.blocksIds) !== idSetKey(next.blocksIds);
  return (
    (!classroomsChanged || !!abilities.can_assign_classrooms) &&
    (!instructorsChanged || !!abilities.can_assign_instructors) &&
    (!scheduleChanged || !!abilities.can_assign_blocks) &&
    (row.date === next.date || !!abilities.can_assign_intervals) &&
    (row.sectionId === next.sectionId || canCreateOccurrence(policy))
  );
};
